/**
 * PolicyTriggersAgGridHook
 *
 * AG Grid hook for the "Recent Triggers" table on a single Risk Policy's
 * detail page. Fully read-only (view-only reporting, no actions) — this
 * hook exists purely for layout/column consistency with the rest of the
 * Risk module, not because this small table (max 8 rows) had a
 * horizontal-scroll problem.
 */

// Tailwind classes (not inline hex) so these follow the app's `.theme-dark`
// overrides automatically — matches status_colour that used to live in
// policies.ex before this table moved to AG Grid.
const STATUS_CLASSES = {
  Hold: "bg-orange-100 text-orange-700",
  Alert: "bg-red-100 text-red-700",
  Reviewed: "bg-blue-100 text-blue-700",
  Approved: "bg-green-100 text-green-700",
  Rejected: "bg-gray-200 text-gray-700",
  "Requested Docs": "bg-yellow-100 text-yellow-700",
};
const DEFAULT_TONE_CLASSES = "bg-gray-100 text-gray-600";

const statusBadge = (params) => {
  const value = params.value;
  if (!value) return "";
  const classes = STATUS_CLASSES[value] || DEFAULT_TONE_CLASSES;
  return `<span class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${classes}">${value}</span>`;
};

const PolicyTriggersAgGridHook = {
  mounted() {
    const container = this.el;
    const rows = JSON.parse(container.dataset.rows || "[]");

    const columnDefs = [
      { headerName: "Transaction", field: "transaction_id", pinned: "left", width: 140, cellClass: "font-mono", filter: "agTextColumnFilter" },
      { headerName: "Merchant", field: "merchant_id", flex: 1, minWidth: 150, cellClass: "font-mono" },
      { headerName: "Amount", field: "amount", flex: 0.9, minWidth: 120, type: "numericColumn" },
      { headerName: "Rule", field: "rule_name", flex: 1.5, minWidth: 200, tooltipField: "rule_name" },
      { headerName: "Triggered", field: "triggered_at", flex: 1, minWidth: 150 },
      { headerName: "Status", field: "status", flex: 0.9, minWidth: 120, cellRenderer: statusBadge },
    ];

    const gridOptions = {
      columnDefs,
      rowData: rows,
      defaultColDef: { resizable: true, sortable: true, filter: true, minWidth: 80 },
      animateRows: true,
      rowHeight: 40,
      headerHeight: 36,
      pagination: false,
      domLayout: "normal",
      popupParent: document.body,
      sideBar: {
        toolPanels: [
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
            },
          },
        ],
        defaultToolPanel: "",
      },
      overlayNoRowsTemplate: `<span class="text-sm text-gray-500">No triggers in this window.</span>`,
    };

    if (window.agGrid && window.agGrid.createGrid) {
      this.gridApi = window.agGrid.createGrid(container, gridOptions);
    } else {
      console.error("PolicyTriggersAgGridHook: agGrid not found on window");
    }
  },

  updated() {
    if (this.gridApi && this.el.dataset.rows) {
      try {
        this.gridApi.setGridOption("rowData", JSON.parse(this.el.dataset.rows));
      } catch (e) {
        console.warn("PolicyTriggersAgGridHook updated(): failed to parse rows", e);
      }
    }
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default PolicyTriggersAgGridHook;
